import type { Connector, ConnectorType } from '@/types'

export const CONNECTOR_TYPES: ConnectorType[] = ['GitHub', 'GitLab', 'Bitbucket', 'AzureDevOps', 'Jira', 'Figma', 'SonarQube', 'Anthropic', 'GitHubCopilot', 'QubizTitan']

export const REPOSITORY_CONNECTOR_TYPES: ConnectorType[] = ['GitHub', 'GitLab', 'Bitbucket', 'AzureDevOps']

export const AI_PROVIDER_CONNECTOR_TYPES: ConnectorType[] = ['Anthropic', 'GitHubCopilot', 'QubizTitan']

export function isAiProviderConnector(type: ConnectorType): boolean {
  return AI_PROVIDER_CONNECTOR_TYPES.includes(type)
}

export const ADO_WIKI_CONNECTOR_TYPES: ConnectorType[] = ['AzureDevOps']

export const MULTI_INSTANCE_CONNECTOR_TYPES: ConnectorType[] = ['GitHub', 'GitLab', 'Bitbucket', 'AzureDevOps', 'Jira', 'Figma', 'RelationalDatabase']

export function supportsMultipleInstances(type: ConnectorType): boolean {
  return MULTI_INSTANCE_CONNECTOR_TYPES.includes(type)
}

export function usesInstanceManagement(type: ConnectorType): boolean {
  return supportsMultipleInstances(type) && type !== 'RelationalDatabase'
}

export function isResolvableByTypeAlone(type: ConnectorType, connectors: Connector[]): boolean {
  return connectors.filter((c) => c.type === type && c.isEnabled).length === 1
}

export const CONNECTOR_LABELS: Record<ConnectorType, string> = {
  GitHub: 'GitHub',
  GitLab: 'GitLab',
  Bitbucket: 'Bitbucket',
  AzureDevOps: 'Azure DevOps',
  Jira: 'Jira',
  Figma: 'Figma',
  SonarQube: 'SonarQube',
  RelationalDatabase: 'Relational Database',
  Anthropic: 'Anthropic',
  GitHubCopilot: 'GitHub Copilot',
  QubizTitan: 'Qubiz Titan',
}

export const CONNECTOR_DESCRIPTIONS: Record<ConnectorType, string> = {
  GitHub: 'Connect your GitHub repositories to fetch source code and pull requests.',
  GitLab: 'Connect your GitLab projects to access source code and merge requests.',
  Bitbucket: 'Connect your Bitbucket repositories to sync source code.',
  AzureDevOps: 'Connect Azure DevOps to access repos, boards, and pipelines.',
  Jira: 'Connect Jira to import issues, epics, and project requirements.',
  Figma: 'Connect Figma to access design files and UI specifications.',
  SonarQube: 'Connect SonarQube to import code quality and security metrics.',
  RelationalDatabase: 'Saved database connections used as sources and targets by the database migration workflows.',
  Anthropic: 'Run Claude Code agents with your Anthropic API key for this project.',
  GitHubCopilot: 'Run Copilot CLI agents with your GitHub account in this project.',
  QubizTitan: 'Run agents on the Qubiz-hosted model with your personal key.',
}

export const CONNECTOR_TOKEN_LABELS: Partial<Record<ConnectorType, string>> = {
  GitHub: 'Personal Access Token',
  GitLab: 'Personal Access Token',
  Bitbucket: 'API Token',
  AzureDevOps: 'Personal Access Token',
  Jira: 'API Token',
  Figma: 'Personal Access Token',
  SonarQube: 'Access Token',
  Anthropic: 'API Key',
  GitHubCopilot: 'GitHub Token',
  QubizTitan: 'Personal Key',
}

export const CONNECTOR_OVERVIEWS: Record<ConnectorType, string> = {
  GitHub: 'GitHub is the world\'s leading software development platform. Connecting GitHub allows the pipeline to access your repositories, pull requests, and code reviews. This integration enables automatic code analysis, PR-based delivery tracking, and seamless synchronization of your development workflow.',
  GitLab: 'GitLab is a complete DevOps platform delivered as a single application. Connecting GitLab gives the pipeline access to your projects, merge requests, and CI/CD pipelines. This integration enables end-to-end visibility into your development and deployment processes.',
  Bitbucket: 'Bitbucket is Atlassian\'s Git-based source code repository hosting service. Connecting Bitbucket allows the pipeline to access your repositories and pull requests, enabling code analysis and delivery tracking across your Atlassian toolchain.',
  AzureDevOps: 'Azure DevOps provides developer services for teams to plan work, collaborate on code development, and build and deploy applications. Connecting Azure DevOps gives the pipeline access to your repos, boards, and pipelines for comprehensive project visibility.',
  Jira: 'Jira is the leading project management tool for agile teams. Connecting Jira allows the pipeline to import issues, epics, and sprint data, enabling automatic synchronization of project requirements and delivery progress tracking.',
  Figma: 'Figma is a collaborative interface design tool. Connecting Figma allows the pipeline to access your design files and UI specifications, enabling design-to-development handoff and visual asset tracking throughout the delivery process.',
  SonarQube: 'SonarQube scans repositories to surface code quality, security issues, and technical debt so the delivery pipeline can act on quality gate results.',
  RelationalDatabase: 'Relational database connections store the credentials and location of the source and target databases your migration workflows read from and write to. Each project can hold multiple connections, managed from the Databases section.',
  Anthropic: 'Anthropic provides the Claude models that run Claude Code agents. The API key you connect here is yours and is used only in this project, so each client\'s work runs on that client\'s key. A Claude OAuth token set in My Settings is used before this key.',
  GitHubCopilot: 'GitHub Copilot runs Copilot CLI agents with your GitHub account. The token you connect here is yours and is used only in this project, before any project or organization token.',
  QubizTitan: 'Qubiz Titan is the Qubiz-hosted model gateway. Your organization sets the endpoint; you connect your personal key here, and it is used only in this project.',
}

export const AI_PROVIDER_DISCONNECT_EFFECTS: Partial<Record<ConnectorType, string>> = {
  Anthropic: 'Your Anthropic API key will be removed from this project. Claude Code runs you start here will use your Claude OAuth token, the project key or the organization key, if one is set.',
  GitHubCopilot: 'Your GitHub Copilot token will be removed from this project. Copilot CLI runs you start here will use the project key or the organization key, if one is set.',
  QubizTitan: 'Your Qubiz Titan key will be removed from this project. Titan runs you start here will stop until you connect a key again.',
}

export const CONNECTOR_HOW_IT_WORKS: Record<ConnectorType, string[]> = {
  GitHub: [
    'Click "Enable" to open the token configuration dialog.',
    'Generate a fine-grained Personal Access Token at GitHub > Settings > Developer settings > Personal access tokens > Fine-grained tokens. Select the repositories the pipeline needs and grant Contents (read/write) and Pull requests (write) permissions.',
    'Paste the token to securely connect your GitHub account.',
    'The pipeline will discover and list the repositories your token was granted access to.',
  ],
  GitLab: [
    'Click "Enable" to open the token configuration dialog.',
    'Generate a Personal Access Token in GitLab with "api" and "read_repository" scopes.',
    'Paste the token to securely connect your GitLab account.',
    'The pipeline will discover and list your accessible projects and merge requests.',
  ],
  Bitbucket: [
    'Click "Enable" to open the token configuration dialog.',
    'Create an API Token at id.atlassian.com > Security > API tokens with scopes: read:user:bitbucket, read:workspace:bitbucket, read:repository:bitbucket, write:repository:bitbucket, read:pullrequest:bitbucket, write:pullrequest:bitbucket.',
    'Enter your Atlassian email and the API token to connect.',
    'The pipeline will sync your repository data and pull request activity.',
  ],
  AzureDevOps: [
    'Click "Enable" to open the token configuration dialog.',
    'Generate a Personal Access Token in Azure DevOps with Code (Read) and Wiki (Read & Write) scopes (Wiki is required to add wiki pages as project context and to publish pages from chat).',
    'Paste the token to securely connect your Azure DevOps organization.',
    'The pipeline will access your repositories, wiki pages, work items, and pipeline runs.',
  ],
  Jira: [
    'Click "Enable" to open the token configuration dialog.',
    'Generate an API Token from your Atlassian account settings.',
    'Enter your Jira email and the API token to connect.',
    'The pipeline will import your issues, epics, and sprint data automatically.',
  ],
  Figma: [
    'Click "Enable" to open the token configuration dialog.',
    'Generate a Personal Access Token at Figma > Settings > Security > Personal access tokens with at least Current user (read) and File content (read) access.',
    'Paste the token to securely connect your Figma account.',
    'Design files and components will be available for reference in your workflow.',
  ],
  SonarQube: [
    'Connect SonarQube using an access token or service account.',
    'Select the project and quality gates to monitor.',
    'The pipeline retrieves scan results and flags issues automatically.',
    'Review quality status in pipeline dashboards and project reports.',
  ],
  RelationalDatabase: [
    'Open the Databases section on the project page and click "Add database connection".',
    'Pick the database engine and enter the connection details, or upload a data file for file-based sources.',
    'The connection becomes selectable as a source or target when triggering database migration workflows.',
  ],
  Anthropic: [
    'Click "Enable" to open the connection dialog.',
    'Create an API key at console.anthropic.com and paste it.',
    'The key is checked with Anthropic when you connect; a key that does not work is not saved.',
    'Claude Code runs you start in this project use this key, unless you have a Claude OAuth token in My Settings.',
  ],
  GitHubCopilot: [
    'Click "Enable" to open the connection dialog.',
    'Sign in with GitHub, or paste a fine-grained personal access token (starts with github_pat_) from an account with an active Copilot subscription.',
    'The token is checked with GitHub when you connect; a token that does not work is not saved.',
    'Copilot CLI runs you start in this project use this token.',
  ],
  QubizTitan: [
    'Your organization sets the Titan endpoint in Tenant Settings.',
    'Click "Enable" and paste the personal key you received from the Titan platform team.',
    'Titan can only be reached from your organization\'s run environment, so the key is checked on the first Titan run in this project.',
    'Titan runs you start in this project use this key.',
  ],
}
